// Central content + asset references for the EtherLake site.

export const ASSETS = {
  logoDark:
    "https://static.prod-images.emergentagent.com/jobs/4c91e1cd-0f4a-40cc-965f-06a18fce6e4c/images/f0762b17ed4b6ab25ea34ed8934329e7b22d19466d3e14ef56c4bfff787af426.png",
  heroBg:
    "https://static.prod-images.emergentagent.com/jobs/4c91e1cd-0f4a-40cc-965f-06a18fce6e4c/images/90d71d5039b87f344f2222c1c39a0975737df966719dbfbd38e3d579b1a0faf0.png",
  productAbstract:
    "https://images.unsplash.com/photo-1597773150796-e5c14ebecbf5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  aboutTeam:
    "https://images.unsplash.com/photo-1758691737212-3eebbc8f84ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Apeiron", to: "/product" },
  { label: "Use Cases", to: "/use-cases" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const PROBLEMS = [
  {
    no: "01",
    title: "Operational Complexity",
    body:
      "Proprietary formats, dialect-specific SQL, and tightly coupled toolchains trap teams architecturally. Every feature you adopt deepens the dependency — the exit cost grows every quarter.",
    quote: "Every new capability we adopt pulls us deeper in.",
  },
  {
    no: "02",
    title: "Cost Without Transparency",
    body:
      "Managed platforms charge enterprise rates for capabilities most teams never use, while self-managing explodes total cost through headcount and operational overhead.",
    quote: "We pay for a dozen features. We use exactly none of them.",
  },
  {
    no: "03",
    title: "AI Without Depth",
    body:
      "Legacy lakehouses bolt AI on top of batch-first architecture. Vector stores, model inference, and agent state live in separate systems with no shared lineage — AI becomes shallow, not deep.",
    quote: "Our AI runs on hours-old batch data. It was never built for real-time.",
  },
];

export const FORCES = [
  {
    no: "Force 01",
    title: "Open Formats Win",
    body:
      "Open table formats are now the undisputed standard, backed by every major engine. For the first time, data is engine-agnostic — and a platform built open from day one carries zero format legacy debt.",
    solves: "Solves lock-in by architecture",
  },
  {
    no: "Force 02",
    title: "Data Moves to the Query",
    body:
      "Query federation — running against data in-place across any source without copying it — is becoming the baseline. Every pipeline that exists only to copy data for querying is now a liability.",
    solves: "Solves cost without transparency",
  },
  {
    no: "Force 03",
    title: "Agents Become the Operators",
    body:
      "AI agents no longer just consume data — they operate pipelines. They write transforms, trigger ingestion, detect anomalies, and deploy autonomously. The platform must be agent-native to keep up.",
    solves: "Solves AI without depth",
  },
];

export const LAYERS = [
  {
    name: "Intent Layer",
    tag: "Express what you need",
    items: ["Natural-language prompt", "API / SDK", "Pipeline templates", "Marketplace blueprints"],
  },
  {
    name: "Agent Layer",
    tag: "Agents orchestrate everything",
    items: ["Orchestration engine", "Agent marketplace", "Learning engine", "Control-plane APIs"],
    proprietary: true,
  },
  {
    name: "Platform Layer",
    tag: "Open infrastructure",
    items: ["Open table format", "REST catalog", "Serverless query engines", "Bring-your-own storage"],
  },
  {
    name: "Ecosystem Layer",
    tag: "Integrates with everything you use",
    items: ["Query federation", "200+ connectors", "Transform & streaming tools", "BI & ML frameworks"],
  },
];

export const DIFFERENTIATORS = [
  {
    title: "Agent-first, semi-managed",
    body:
      "AI agents run orchestration, ETL, analytics, and activation end to end from a single stated intent — instead of wiring each step by hand.",
  },
  {
    title: "Intent-driven for every persona",
    body:
      "The same platform serves engineers writing SQL and business stakeholders asking plain-language questions — no separate tools, no hand-offs.",
  },
  {
    title: "Open standards, OSS core",
    body:
      "Built on an open table format and open-source engines — never a proprietary storage or execution layer you can't leave.",
  },
  {
    title: "Bring-your-own-cloud",
    body:
      "Compute and storage run inside your own cloud account, accessed over private paths. Your data stays sovereign — never copied into a vendor silo.",
  },
  {
    title: "Cost transparency before you run",
    body:
      "Agents estimate cost and show parsed intent before execution, so you approve spend instead of discovering it on a bill.",
  },
  {
    title: "Maintenance is automatic",
    body:
      "Compaction, snapshot expiration, and orphan-file cleanup run as background jobs out of the box — no cron scripts to babysit.",
  },
];

export const CAPABILITIES = [
  {
    title: "Runs agents end to end",
    body:
      "Agents orchestrate the full lifecycle — ingestion, transformation, analytics, and activation — from a single stated intent, not a chain of hand-built pipelines.",
  },
  {
    title: "Understands intent, not just SQL",
    body:
      "An engineer writes table DDL/DML while a manager asks in plain language — both reach the same governed data.",
  },
  {
    title: "Executes fast, distributed queries",
    body:
      "A distributed engine runs interactive analytics directly against open tables — joins, aggregations, and MERGE/UPDATE/DELETE at scale.",
  },
  {
    title: "Manages the catalog for you",
    body:
      "An API-driven REST catalog tracks every table's schema, snapshots, and metadata, with atomic commits so concurrent writers never corrupt data.",
  },
  {
    title: "Keeps tables fast automatically",
    body:
      "Background jobs compact small files, expire old snapshots, and clean orphaned files — maintenance most lakehouses leave to you.",
  },
  {
    title: "Shows its work before running it",
    body:
      "Every agent action surfaces its parsed intent and estimated cost up front, so a human stays in the loop on spend and outcome.",
  },
  {
    title: "Keeps your data sovereign",
    body:
      "Data lives in your own cloud storage and account, accessed over private network paths — never copied into a vendor-controlled silo.",
  },
];

export const PERSONAS = [
  { role: "Startups, SMBs & mid-tier", body: "Warehouse-grade analytics and governance without warehouse-grade cost or headcount." },
  { role: "Data Engineers & Analysts", body: "Full SQL and open-table control, with agents handling repetitive orchestration and pipeline work." },
  { role: "Data Scientists", body: "Governed, queryable access to the same open tables for modeling — no copying into a separate environment." },
  { role: "Business & C-suite", body: "Direct, intent-driven access to insight and activation — no SQL required, no waiting on a queue." },
];

export const USE_CASES = [
  {
    industry: "Fintech",
    title: "Real-Time Fraud Detection",
    profile: "Series B · 380 employees · 6 data engineers · cloud-native",
    expressed: [
      "Connect transaction event streams and customer profile data",
      "Keep fraud features fresh in real time",
      "Make data available to our fraud model",
      "Alert us when data quality drops",
    ],
    orchestrated: [
      "Streaming CDC into open tables, sub-second",
      "Real-time feature computation agent from the marketplace",
      "Catalog endpoint registered for the model runtime",
      "Data-quality monitoring agent on every table",
      "Model-artifact tracking wired in automatically",
    ],
    outcome: "Feature-store and streaming tooling eliminated · three monitoring tools collapsed into one agent-native platform.",
  },
  {
    industry: "Healthcare",
    title: "Patient Risk Stratification",
    profile: "Regional health system · 1,200 staff · 8 data engineers · cloud-native",
    expressed: [
      "Ingest clinical and claims data with full compliance",
      "Classify all sensitive data automatically — no manual tagging",
      "Build patient risk scores for care managers",
      "Generate compliance audit reports in minutes, not weeks",
    ],
    orchestrated: [
      "Clinical-standard connector agent streaming into open tables",
      "Automatic sensitive-data classification at ingest",
      "Cell-level security policies applied automatically",
      "Compliance pack: policies, audit logs, right-to-erasure",
      "Models read tables in place — data never leaves your account",
    ],
    outcome: "Zero manual tagging hours · audit assembly from three weeks to minutes · data never leaves the customer's cloud.",
  },
  {
    industry: "Retail",
    title: "AI-Powered Demand Forecasting",
    profile: "DTC e-commerce · $120M GMV · 600 staff · 5 data engineers · cloud-native",
    expressed: [
      "Unify point-of-sale, inventory, promotions, and weather signals",
      "Train a demand-forecasting model updated daily",
      "Let merchandising query forecasts directly",
      "Write forecast outputs back to operational systems",
    ],
    orchestrated: [
      "Streaming POS events into open tables",
      "ERP and CRM connector agents with real-time inventory",
      "Transform agent running SQL on open tables",
      "Model reads tables directly — zero-copy federation back to ops systems",
    ],
    outcome: "Ingestion and reverse-ETL tooling eliminated · batch lag removed · four vendor contracts collapsed to one.",
    videoId: "fo8SFA7aNOk",
  },
];

export const STATS = [
  { value: "100%", label: "Open table format, from day one" },
  { value: "0", label: "Proprietary storage formats" },
  { value: "200+", label: "Source connectors via federation" },
  { value: "1", label: "Platform replacing the stack" },
];
